<div class="hc" id="spectrum-help-top">
    <header class="hc-head">
        <div>
            <div class="hc-crumb">Help centre · <b>{{ manual.title }}</b></div>
            <h1>{{ manual.title }}</h1>
        </div>
        <div class="hc-head-actions">
            <label class="hc-search">
                <i class="fas fa-magnifying-glass"></i>
                <input type="search" [(ngModel)]="search" placeholder="Search the manual…" aria-label="Search the manual" />
            </label>
            <button type="button" class="hc-btn" (click)="print()" title="The whole manual, ready to print or save as PDF">
                <i class="fas fa-print"></i> Print manual
            </button>
        </div>
    </header>

    <div class="hc-grid">
        <nav class="hc-toc" aria-label="Contents">
            <div class="hc-toc-h">Manuals</div>
            @for (m of manuals; track m.module) {
                <a [routerLink]="['/help', m.module]" [class.on]="m === manual">{{ m.title }}</a>
            }
            <div class="hc-toc-h">{{ manual.title }}</div>
            <a (click)="go('intro')" [class.on]="isOn('intro')" href="javascript:void(0)">Introduction</a>
            @for (c of manual.chapters; track c.id) {
                <a (click)="go(c.id)" [class.on]="isOn(c.id)" href="javascript:void(0)">{{ c.title }}</a>
            }
            @if (manual.workflows.length || manual.roles?.length || manual.glossary.length) {
                <div class="hc-toc-h">Reference</div>
            }
            @if (manual.workflows.length) {
                <a (click)="go('walkthroughs')" [class.on]="isOn('walkthroughs')" href="javascript:void(0)">Walkthroughs</a>
            }
            @if (manual.roles?.length) {
                <a (click)="go('roles')" [class.on]="isOn('roles')" href="javascript:void(0)">Roles &amp; access</a>
            }
            @if (manual.glossary.length) {
                <a (click)="go('glossary')" [class.on]="isOn('glossary')" href="javascript:void(0)">Glossary</a>
            }
            <a (click)="go('all')" [class.on]="isOn('all')" href="javascript:void(0)">The whole manual on one page</a>
        </nav>

        <article class="hc-doc">
            @if (search.trim().length > 1) {
                <h2>Results for “{{ search.trim() }}”</h2>
                <div class="hc-hits">
                    @for (hit of hits; track hit.ref.topic.id) {
                        <button type="button" class="hc-hit" (click)="openHit(hit)">
                            <b [innerHTML]="line(hit.label)"></b>
                            <small>{{ hit.ref.topic.title }}@if (hit.ref.manual.title !== hit.ref.topic.title) { · {{ hit.ref.manual.title }} }</small>
                        </button>
                    } @empty {
                        <p class="hc-muted">Nothing matches. Try other words — the name of a button, a column, or a message you saw.</p>
                    }
                </div>
            } @else if (!guide.manualReady && !guide.manualFailed && manual === guide.registry.basics && guide.enabled) {
                <p class="hc-muted"><i class="fas fa-spinner fa-spin"></i> Opening the manual…</p>
            } @else {
                @switch (section.kind) {
                    @case ('intro') {
                        <div class="hc-lede" [innerHTML]="md(manual.intro)"></div>
                        <div class="hc-cards">
                            @for (c of manual.chapters; track c.id) {
                                <button type="button" class="hc-card" (click)="go(c.id)">
                                    <b>{{ c.title }}</b>
                                    <small>{{ c.intro }}</small>
                                    <span>{{ topicsOf(c).length }} {{ topicsOf(c).length === 1 ? 'page' : 'pages' }}</span>
                                </button>
                            }
                        </div>
                        @if (manual.workflows.length) {
                            <h2>Walkthroughs</h2>
                            <p class="hc-muted">Jobs that cross several screens. Start one and the Guide takes you from screen to screen, keeping your place.</p>
                            <div class="hc-cards">
                                @for (w of manual.workflows.slice(0, 6); track w.id) {
                                    <button type="button" class="hc-card" (click)="startWorkflow(w)">
                                        <b>{{ w.title }}</b>
                                        <small>{{ w.goal }}</small>
                                        <span>Start · {{ w.steps.length }} steps</span>
                                    </button>
                                }
                            </div>
                            @if (manual.workflows.length > 6) {
                                <button type="button" class="hc-link" (click)="go('walkthroughs')">All {{ manual.workflows.length }} walkthroughs</button>
                            }
                        }
                    }

                    @case ('walkthroughs') {
                        <h2>Walkthroughs</h2>
                        <p class="hc-muted">Start one and the Guide pins it: each step says where to go, and your place is kept.</p>
                        @for (w of manual.workflows; track w.id) {
                            <section class="hc-topic">
                                <div class="hc-topic-head">
                                    <h3>{{ w.title }}</h3>
                                    <button type="button" class="hc-btn" (click)="startWorkflow(w)">Start <i class="fas fa-arrow-right"></i></button>
                                </div>
                                <p><b>Goal:</b> {{ w.goal }}@if (w.cadence) { <br /><b>When:</b> {{ w.cadence }} }</p>
                                <ol>
                                    @for (s of w.steps; track $index) {
                                        <li><span [innerHTML]="line(s.text)"></span>
                                            @if (s.topic && topicTitle(s.topic)) {
                                                · <a [routerLink]="['/help', manual.module]" [queryParams]="topicLink(s.topic)">{{ topicTitle(s.topic) }}</a>
                                            }
                                        </li>
                                    }
                                </ol>
                            </section>
                        }
                    }

                    @case ('roles') {
                        <h2>Roles &amp; access</h2>
                        <p class="hc-muted">Who does what in {{ manual.title }}. Access is granted by the school's administrator in the Security module.</p>
                        <dl class="hc-terms">
                            @for (r of manual.roles || []; track $index) { <dt [innerHTML]="line(r.term)"></dt><dd [innerHTML]="line(r.meaning)"></dd> }
                        </dl>
                    }

                    @case ('glossary') {
                        <h2>Glossary</h2>
                        <dl class="hc-terms">
                            @for (g of sortedGlossary; track g.term) { <dt [innerHTML]="line(g.term)"></dt><dd [innerHTML]="line(g.meaning)"></dd> }
                        </dl>
                    }

                    @default {
                        @if (section.kind === 'all') {
                            <div class="hc-lede" [innerHTML]="md(manual.intro)"></div>
                        }
                        @for (chapter of shownChapters; track chapter.id) {
                            <h2 class="hc-chapter">{{ chapter.title }}</h2>
                            <p class="hc-lede">{{ chapter.intro }}</p>
                            @if (section.kind === 'chapter') {
                                <ul class="hc-mini-toc">
                                    @for (t of topicsOf(chapter); track t.id) {
                                        <li><a href="javascript:void(0)" (click)="scrollTo(t.id)">{{ t.title }}</a></li>
                                    }
                                </ul>
                            }
                            @for (t of topicsOf(chapter); track t.id) {
                                <section class="hc-topic" [id]="anchor(t.id)">
                                    <div class="hc-topic-head">
                                        <h3>{{ t.title }}</h3>
                                        @if (screenRoute(t)) {
                                            <button type="button" class="hc-btn" (click)="openScreen(t)">Open the screen <i class="fas fa-arrow-right"></i></button>
                                        }
                                    </div>
                                    <div [innerHTML]="md(t.summary)"></div>
                                    <dl class="hc-facts">
                                        @if (t.audience) { <dt>Who uses it</dt><dd [innerHTML]="line(t.audience)"></dd> }
                                        @if (t.access) { <dt>Access</dt><dd [innerHTML]="line(t.access)"></dd> }
                                        @if (t.reach) { <dt>How to get there</dt><dd [innerHTML]="line(t.reach)"></dd> }
                                    </dl>
                                    @for (task of t.tasks; track $index) {
                                        <h4>{{ task.title }}</h4>
                                        @if (task.when) { <p class="hc-muted">{{ task.when }}</p> }
                                        <ol>@for (step of task.steps; track $index) { <li [innerHTML]="line(step)"></li> }</ol>
                                        @if (task.result) { <p class="hc-result"><b>Result: </b><span [innerHTML]="line(task.result)"></span></p> }
                                    }
                                    @if (t.fields?.length) {
                                        <h4>What you see</h4>
                                        <dl class="hc-terms">
                                            @for (f of t.fields; track $index) { <dt [innerHTML]="line(f.term)"></dt><dd [innerHTML]="line(f.meaning)"></dd> }
                                        </dl>
                                    }
                                    @if (t.cautions?.length) {
                                        <h4>Watch out for</h4>
                                        @for (c of t.cautions; track $index) { <div class="hc-caution" [innerHTML]="line(c)"></div> }
                                    }
                                    @if (t.tips?.length) {
                                        <h4>Tips</h4>
                                        <ul>@for (tip of t.tips; track $index) { <li [innerHTML]="line(tip)"></li> }</ul>
                                    }
                                    @if (t.errors?.length) {
                                        <h4>Messages and what to do</h4>
                                        <div class="hc-table">
                                            <table>
                                                <thead><tr><th>Message</th><th>What it means</th><th>What to do</th></tr></thead>
                                                <tbody>
                                                    @for (e of t.errors; track $index) {
                                                        <tr>
                                                            <td>@if (e.code) { <code>{{ e.code }}</code><br /> }<span [innerHTML]="line(e.message)"></span></td>
                                                            <td [innerHTML]="line(e.meaning)"></td>
                                                            <td [innerHTML]="line(e.fix)"></td>
                                                        </tr>
                                                    }
                                                </tbody>
                                            </table>
                                        </div>
                                    }
                                    @if (t.faqs?.length) {
                                        <h4>Questions people ask</h4>
                                        @for (f of t.faqs; track $index) {
                                            <details class="hc-faq" [attr.open]="section.kind === 'all' ? '' : null"><summary>{{ f.q }}</summary><div [innerHTML]="md(f.a)"></div></details>
                                        }
                                    }
                                    @if (t.related?.length) {
                                        <p class="hc-see">See also:
                                            @for (id of t.related; track id; let last = $last) {
                                                @if (topicTitle(id)) {
                                                    <a [routerLink]="['/help', manual.module]" [queryParams]="topicLink(id)">{{ topicTitle(id) }}</a>@if (!last) { · }
                                                }
                                            }
                                        </p>
                                    }
                                </section>
                            }
                        }
                        @if (section.kind === 'all') {
                            @if (manual.workflows.length) {
                                <h2 class="hc-chapter">Walkthroughs</h2>
                                @for (w of manual.workflows; track w.id) {
                                    <section class="hc-topic">
                                        <h3>{{ w.title }}</h3>
                                        <p><b>Goal:</b> {{ w.goal }}@if (w.cadence) { <br /><b>When:</b> {{ w.cadence }} }</p>
                                        <ol>
                                            @for (s of w.steps; track $index) {
                                                <li><span [innerHTML]="line(s.text)"></span>@if (s.topic && topicTitle(s.topic)) { · <i>{{ topicTitle(s.topic) }}</i> }</li>
                                            }
                                        </ol>
                                    </section>
                                }
                            }
                            @if (manual.roles?.length) {
                                <h2 class="hc-chapter">Roles &amp; access</h2>
                                <dl class="hc-terms">
                                    @for (r of manual.roles || []; track $index) { <dt [innerHTML]="line(r.term)"></dt><dd [innerHTML]="line(r.meaning)"></dd> }
                                </dl>
                            }
                            @if (manual.glossary.length) {
                                <h2 class="hc-chapter">Glossary</h2>
                                <dl class="hc-terms">
                                    @for (g of sortedGlossary; track g.term) { <dt [innerHTML]="line(g.term)"></dt><dd [innerHTML]="line(g.meaning)"></dd> }
                                </dl>
                            }
                        }
                    }
                }
            }
        </article>
    </div>
</div>
